import { all, fork, put, takeEvery, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";

// helpers
import {
  getInvoices as getInvoiceList,
  getInvoice as getInvoiceApi,
  updateStatusInvoiceApi,
  updateInvoiceApi,
} from "../../helpers/";

// constants
import { InvoicesActionTypes } from "./constants";
import {
  InvoicesGetListError,
  InvoicesGetListSuccess,
  getInvoiceSuccess,
  getInvoiceError,
  updateStatusInvoiceSuccess,
  updateStatusInvoiceError,
  updateInvoiceSuccess,
  updateInvoiceError,
} from "./actions";

interface InvoiceData {
  payload: {
    limit: number;
    skip: number;
    keyword?: string;
    owner?: string;
    country?: string;
    status?: string;
    boutique?: string;
  };
  type: string;
}

interface InvoiceUpdateData {
  payload: {
    id: string;
    expeditionFees: number;
    otherFees: number;
  };
  type: string;
}

interface InvoiceStatusUpdateData {
  payload: {
    id: string;
    status?: string;
  };
  type: string;
}

interface GetInvoiceData {
  payload: {
    id: string;
  };
  type: string;
}

function* getInvoices({
  payload: { limit, skip, keyword, owner, country, status, boutique },
}: InvoiceData): SagaIterator {
  try {
    const response = yield call(getInvoiceList, {
      limit,
      skip,
      keyword,
      owner,
      country,
      status,
      boutique,
    });
    // Voir orders/saga : on ignore 403 silencieux et data null pour ne pas
    // écraser la liste à l'écran.
    if ((response as any)?.__handled403) return;
    const Invoices = response.data;
    if (Invoices == null) return;
    yield put(
      InvoicesGetListSuccess(InvoicesActionTypes.GET_LIST_INVOICES, Invoices)
    );
  } catch (error: any) {
    yield put(
      InvoicesGetListError(InvoicesActionTypes.GET_LIST_INVOICES, error)
    );
  }
}

function* updateStatusInvoice({
  payload: { id, status },
}: InvoiceStatusUpdateData): SagaIterator {
  try {
    const response = yield call(updateStatusInvoiceApi, {
      id,
      status,
    });
    const Invoice = response.data;
    yield put(
      updateStatusInvoiceSuccess(
        InvoicesActionTypes.UPDATE_STATUS_INVOICE,
        Invoice
      )
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response.data;
    yield put(
      updateStatusInvoiceError(
        InvoicesActionTypes.UPDATE_STATUS_INVOICE,
        errorMessage
      )
    );
  }
}

function* updateInvoice({
  payload: { id, expeditionFees, otherFees },
}: InvoiceUpdateData): SagaIterator {
  try {
    const response = yield call(updateInvoiceApi, {
      id,
      expeditionFees,
      otherFees,
    });
    const invoice = response.data;
    yield put(
      updateInvoiceSuccess(InvoicesActionTypes.UPDATE_INVOICE, invoice)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response.data;
    yield put(
      updateInvoiceError(InvoicesActionTypes.UPDATE_INVOICE, errorMessage)
    );
  }
}

function* getInvoice({ payload: { id } }: GetInvoiceData): SagaIterator {
  try {
    const response = yield call(getInvoiceApi, { id });
    const Invoice = response.data;
    yield put(getInvoiceSuccess(InvoicesActionTypes.GET_INVOICE, Invoice));
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response.data;
    yield put(getInvoiceError(InvoicesActionTypes.GET_INVOICE, errorMessage));
  }
}

export function* watchGetInvoices() {
  yield takeEvery(InvoicesActionTypes.GET_LIST_INVOICES, getInvoices);
}

export function* watchUpdateStatusInvoices() {
  yield takeEvery(
    InvoicesActionTypes.UPDATE_STATUS_INVOICE,
    updateStatusInvoice
  );
}

export function* watchGetInvoice() {
  yield takeEvery(InvoicesActionTypes.GET_INVOICE, getInvoice);
}

export function* watchUpdateInvoice() {
  yield takeEvery(InvoicesActionTypes.UPDATE_INVOICE, updateInvoice);
}

function* invoicesSaga() {
  yield all([
    fork(watchGetInvoices),
    fork(watchGetInvoice),
    fork(watchUpdateStatusInvoices),
    fork(watchUpdateInvoice),
  ]);
}

export default invoicesSaga;
